import { useEffect, useState, useSyncExternalStore } from 'react'

const subscribeToVisibilityChange = (onStoreChange: () => void) => {
  document.addEventListener('visibilitychange', onStoreChange)

  return () => {
    document.removeEventListener('visibilitychange', onStoreChange)
  }
}

const getVisibilityChangeSnapshot = () => {
  return document.visibilityState
}

const getVisibilityChangeServerSnapshot = () => {
  throw Error('useVisibilityChange is a client-only hook')
}

/** `true` while the document is visible, re-renders on every `visibilitychange` */
export function useVisibilityChange() {
  const visibilityState = useSyncExternalStore(
    subscribeToVisibilityChange,
    getVisibilityChangeSnapshot,
    getVisibilityChangeServerSnapshot
  )

  return visibilityState === 'visible'
}

/**
 * `true` once the document has been visible since the component mounted and
 * stays `true` after it gets hidden again. Use it to defer work until the app
 * is on screen without tearing that work down on every tab switch.
 */
export function useHasBeenVisible() {
  const isVisible = useVisibilityChange()
  const [hasBeenVisible, setHasBeenVisible] = useState(isVisible)

  useEffect(() => {
    if (isVisible) setHasBeenVisible(true)
  }, [isVisible])

  return hasBeenVisible
}
